/**
 * SIDEBAR — TailAdmin (MIT) birebir davranış: 290px genişletilmiş / 90px daraltılmış,
 * daraltılmışken üzerine gelince geçici açılır (hover), mobilde çekmece, light/dark.
 * Geolop'a özel parçalar korunur: aktif site seçici, Geolop Asistanı orb'u, profil.
 */
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Network, Waypoints, Map, LayoutDashboard, Link2, ClipboardList, BarChart3, Settings,
  Lightbulb, Zap, LogOut, Languages, Clapperboard, BrainCircuit,
  Crosshair, ShieldCheck, MoreHorizontal, Target, FileChartColumn, BotMessageSquare,
  ScanSearch, KeyRound, Braces, ShieldAlert, Gauge, Leaf, Sparkles, Newspaper, Flame, ChevronDown, History,
} from "lucide-react";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { logout } from "@/lib/auth";
import { useMe } from "@/hooks/use-me";
import { AssistantOrb } from "@/components/assistant-orb";
import { assistantStore, useAssistantStore } from "@/lib/assistant-store";
import { shellStore, useShell } from "@/lib/ui-shell-store";

type NavItem = { id: string; label: string; icon: typeof Zap; href: (id: number | null) => string; match: (p: string) => boolean };

type NavGroup = { title: string; items: NavItem[]; daraltilabilir?: boolean };

/**
 * ÇEKİDÜZEN Adım 2 — menü GÖREV DİLİYLE gruplanır ("hangi ajan" değil "ne yapmak istiyorum"):
 *   Genel Bakış: durumum ne? · Ne Yapmalıyım: sıradaki iş · Üret: içerik hattı (akış sırasıyla)
 *   Ölç & Kanıtla: sonuç takibi · Uzman Araçları: derin ekranlar (varsayılan kapalı, hiçbir şey silinmedi)
 */
const GROUPS: NavGroup[] = [
  {
    title: "Genel Bakış",
    items: [
      { id: "genel-bakis", label: "Genel Bakış", icon: LayoutDashboard, href: () => "/", match: (p) => p === "/" || p.startsWith("/kokpit") || p.startsWith("/portfolio") },
      { id: "nobetci", label: "Nöbetçi", icon: ShieldAlert, href: () => "/nobetci", match: (p) => p.startsWith("/nobetci") },
      { id: "assistant", label: "Geolop Asistanı", icon: BrainCircuit, href: () => "/assistant", match: (p) => p.startsWith("/assistant") },
    ],
  },
  {
    title: "Ne Yapmalıyım?",
    items: [
      { id: "firsatlar", label: "Fırsatlar", icon: Flame, href: () => "/firsatlar", match: (p) => p.startsWith("/firsatlar") },
      { id: "planning", label: "Planlama", icon: Crosshair, href: () => "/planning", match: (p) => p.startsWith("/planning") || p.startsWith("/action-plan") || p.startsWith("/content-calendar") },
      { id: "yol-haritasi", label: "Yol Haritası", icon: Map, href: () => "/yol-haritasi", match: (p) => p.startsWith("/yol-haritasi") },
    ],
  },
  {
    // İçerik hattı AKIŞ SIRASIYLA: kelimeyi bul → brief çıkar → üret → güçlendir → çevir → taze tut
    title: "Üret",
    items: [
      { id: "content-studio", label: "İçerik Stüdyosu", icon: Clapperboard, href: () => "/content-studio", match: (p) => p.startsWith("/content-studio") },
      { id: "keyword-research", label: "1. Kelime Araştırma", icon: KeyRound, href: () => "/keyword-research", match: (p) => p.startsWith("/keyword-research") },
      { id: "content-planner", label: "2. Brief Keşif Ajanı", icon: ClipboardList, href: () => "/content-planner", match: (p) => p.startsWith("/content-planner") || p.startsWith("/planner-") },
      { id: "content-opps", label: "3. İçerik Üretim Ajanı", icon: Lightbulb, href: () => "/content-opportunities", match: (p) => p.startsWith("/content-opportunities") || p.startsWith("/content-detail") },
      { id: "page-strengthen", label: "4. Sayfa Güçlendirme", icon: Zap, href: () => "/page-strengthen", match: (p) => p.startsWith("/page-strengthen") },
      { id: "content-translate", label: "5. Çeviri Ajanı", icon: Languages, href: () => "/content-translate", match: (p) => p.startsWith("/content-translate") },
      { id: "tazelik", label: "6. İçerik Tazeliği", icon: Leaf, href: () => "/tazelik", match: (p) => p.startsWith("/tazelik") },
    ],
  },
  {
    title: "Ölç & Kanıtla",
    items: [
      { id: "rank-tracking", label: "Sıralama Takibi", icon: Target, href: () => "/rank-tracking", match: (p) => p.startsWith("/rank-tracking") },
      { id: "ai-firsatlar", label: "AI Fırsatları", icon: Sparkles, href: () => "/ai-firsatlar", match: (p) => p.startsWith("/ai-firsatlar") },
      { id: "seo-report", label: "SEO-GEO Raporu", icon: FileChartColumn, href: () => "/seo-report", match: (p) => p.startsWith("/seo-report") },
      { id: "traffic", label: "Trafik Analizi", icon: BarChart3, href: () => "/traffic-analysis", match: (p) => p.startsWith("/traffic-analysis") },
      { id: "index-radar", label: "Dizin Radarı", icon: ScanSearch, href: () => "/index-radar", match: (p) => p.startsWith("/index-radar") },
      { id: "icerik-gunlugu", label: "İçerik Günlüğü", icon: History, href: () => "/icerik-gunlugu", match: (p) => p.startsWith("/icerik-gunlugu") },
      { id: "sxo", label: "SXO", icon: Gauge, href: () => "/sxo", match: (p) => p.startsWith("/sxo") },
    ],
  },
  {
    title: "Uzman Araçları",
    daraltilabilir: true,
    // Sıralama bilinçli: önce site sağlığı, sonra iç yapı (link/cluster/mimari),
    // sonra anlam-güven etiketleri (schema/E-E-A-T), sonra AI bot çifti (izin→ziyaret),
    // sonra backlink çifti (denetim→fırsat), en sonda bağımsız hızlı analiz.
    items: [
      { id: "tech-audit", label: "Teknik SEO Ajanı", icon: Settings, href: () => "/tech-audit", match: (p) => p.startsWith("/tech-audit") },
      { id: "pages", label: "Link İnşası", icon: Link2, href: (id) => (id ? `/domains/${id}` : "/"), match: (p) => /^\/domains\/\d+$/.test(p) || p.startsWith("/pages/") },
      { id: "cluster-maps", label: "Cluster Maps", icon: Network, href: (id) => (id ? `/domains/${id}/clusters` : "/"), match: (p) => p.includes("/clusters") },
      { id: "mimari-agent", label: "Mimari Ajanı", icon: Waypoints, href: () => "/mimari-agent", match: (p) => p.startsWith("/mimari-agent") },
      { id: "schema-agent", label: "Schema Ajanı", icon: Braces, href: () => "/schema-agent", match: (p) => p.startsWith("/schema-agent") },
      { id: "eeat-agent", label: "E-E-A-T Ajanı", icon: ShieldCheck, href: () => "/eeat-agent", match: (p) => p.startsWith("/eeat-agent") },
      { id: "ai-bot-erisim", label: "AI Bot Erişimi", icon: ShieldCheck, href: () => "/ai-bot-erisim", match: (p) => p.startsWith("/ai-bot-erisim") },
      { id: "bot-log", label: "AI Bot Analizi", icon: BotMessageSquare, href: () => "/bot-log", match: (p) => p.startsWith("/bot-log") },
      { id: "backlink-audit", label: "Backlink Ajanı", icon: Link2, href: () => "/backlink-audit", match: (p) => p.startsWith("/backlink-audit") },
      { id: "backlink-firsat", label: "Backlink Fırsat Ajanı", icon: Newspaper, href: () => "/backlink-firsat", match: (p) => p.startsWith("/backlink-firsat") },
      { id: "quick-analysis", label: "Hızlı Analiz", icon: Zap, href: () => "/quick-analysis", match: (p) => p.startsWith("/quick-analysis") },
    ],
  },
];

export function Sidebar() {
  const [location, setLocation] = useLocation();
  const { data: domains } = useListDomains();
  const domainIdMatch = location.match(/\/domains\/(\d+)/);
  const [activeDomainId, setActiveDomainId] = useActiveDomain(domains, domainIdMatch ? parseInt(domainIdMatch[1]) : null);
  const { expanded, hovered, mobileOpen, sidebarWide } = useShell();

  const { data: adminCheck } = useQuery({
    queryKey: ["admin-check"],
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () => { const r = await fetch("/api/admin/check"); return r.ok ? (r.json() as Promise<{ admin: boolean }>) : { admin: false }; },
  });

  const switchDomain = (id: number) => {
    setActiveDomainId(id);
    if (/^\/domains\/\d+\/clusters/.test(location)) setLocation(`/domains/${id}/clusters`);
    else if (/^\/domains\/\d+$/.test(location)) setLocation(`/domains/${id}`);
  };

  const adminItems: NavItem[] = [
    ...(adminCheck?.admin ? [{ id: "admin", label: "Admin Paneli", icon: ShieldCheck, href: () => "/admin", match: (p: string) => p.startsWith("/admin") } as NavItem] : []),
    { id: "settings", label: "Ayarlar", icon: Settings, href: () => "/settings", match: (p: string) => p.startsWith("/settings") },
  ];
  const groups: NavGroup[] = [...GROUPS, { title: "Yönetim", items: adminItems }];

  // Uzman Araçları varsayılan AÇIK — kullanıcı isterse başlıktan kapatır, tercihi hatırlanır;
  // içindeki bir ekran açıkken grup her durumda görünür kalır (kullanıcı kaybolmasın).
  const [uzmanAcik, setUzmanAcik] = useState(() => {
    try { return localStorage.getItem("geolop:uzman-menu") !== "kapali"; } catch { return true; }
  });
  const uzmanToggle = () => {
    setUzmanAcik((v) => {
      try { localStorage.setItem("geolop:uzman-menu", v ? "kapali" : "acik"); } catch { /* gizli mod */ }
      return !v;
    });
  };

  return (
    <aside
      onMouseEnter={() => !expanded && shellStore.setHovered(true)}
      onMouseLeave={() => shellStore.setHovered(false)}
      className={`fixed left-0 top-0 z-50 flex h-screen flex-col border-r border-gray-200 bg-white transition-all duration-300 ease-in-out dark:border-gray-800 dark:bg-gray-900
        ${sidebarWide ? "w-[290px]" : "w-[90px]"}
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
    >
      {/* Logo — daraltılmışken yalnız simge */}
      <div className={`flex items-center gap-2.5 border-b border-gray-200 py-5 dark:border-gray-800 ${sidebarWide ? "px-6" : "justify-center px-2"}`}>
        <Link href="/">
          <span className="flex cursor-pointer items-center">
            {sidebarWide
              ? <>
                  <img src="/geolop-logo.svg" alt="Geolop" className="h-10 w-auto dark:hidden" />
                  <img src="/geolop-logo-light.svg" alt="Geolop" className="hidden h-10 w-auto dark:block" />
                </>
              : <img src="/geolop-icon.svg" alt="Geolop" className="h-9 w-9" />}
          </span>
        </Link>
      </div>

      {/* Aktif site seçici — daraltılmışken gizli */}
      {sidebarWide && (
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
          <label className="mb-1.5 block text-theme-xs font-semibold uppercase tracking-wide text-gray-400">Aktif Site</label>
          <select
            value={activeDomainId ?? ""}
            onChange={(e) => { const id = parseInt(e.target.value); if (Number.isFinite(id)) switchDomain(id); }}
            className="w-full cursor-pointer rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-theme-sm font-medium text-gray-800 outline-none focus:border-brand-300 focus:shadow-focus-ring dark:border-gray-700 dark:bg-gray-800 dark:text-white/90"
          >
            {(domains ?? []).length === 0 && <option value="">Site yok</option>}
            {(domains ?? []).map((d) => (
              <option key={d.id} value={d.id}>{d.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}</option>
            ))}
          </select>
        </div>
      )}

      {/* Menü */}
      <nav className="no-scrollbar flex-1 overflow-y-auto px-4 py-4">
        {groups.map((g) => {
          const icindeAktifVar = g.items.some((i) => i.match(location));
          // daraltılmış (ikon) modda gizleme yok — orada zaten başlıklar görünmez
          const kapali = !!g.daraltilabilir && sidebarWide && !uzmanAcik && !icindeAktifVar;
          return (
          <div key={g.title} className="mb-5 last:mb-0">
            {g.daraltilabilir && sidebarWide ? (
              <button onClick={uzmanToggle}
                className="mb-2 flex w-full items-center justify-between px-2 text-theme-xs font-semibold uppercase leading-5 tracking-wide text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                {g.title}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${kapali ? "-rotate-90" : ""}`} />
              </button>
            ) : (
              <h3 className={`mb-2 flex text-theme-xs font-semibold uppercase leading-5 tracking-wide text-gray-400 ${sidebarWide ? "px-2" : "justify-center"}`}>
                {sidebarWide ? g.title : <MoreHorizontal className="h-4 w-4" />}
              </h3>
            )}
            {kapali ? null : (
            <ul className="flex flex-col gap-0.5">
              {g.items.map(({ id, label, icon: Icon, href, match }) => {
                const isActive = match(location);
                return (
                  <li key={id}>
                    <Link href={href(activeDomainId)}>
                      <span
                        title={sidebarWide ? undefined : label}
                        onClick={() => shellStore.closeMobile()}
                        className={`menu-item group cursor-pointer ${isActive ? "menu-item-active" : "menu-item-inactive"} ${sidebarWide ? "justify-start" : "justify-center"}`}
                      >
                        <Icon className={`h-5 w-5 shrink-0 ${isActive ? "menu-item-icon-active" : "menu-item-icon-inactive"}`} />
                        {sidebarWide && <span className="truncate">{label}</span>}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
            )}
          </div>
          );
        })}
      </nav>

      <AssistantOrbButton wide={sidebarWide} />
      <UserProfile wide={sidebarWide} />
    </aside>
  );
}

// Geolop Asistanı — TIK = sesli mod. Daraltılmışken küçük orb, genişken büyük kart.
function AssistantOrbButton({ wide }: { wide: boolean }) {
  const { orb, open, voice } = useAssistantStore();
  const status = voice
    ? orb === "listening" ? "dinliyorum…" : orb === "speaking" ? "konuşuyor…" : orb === "thinking" ? "düşünüyor…" : "sesli mod açık, konuş"
    : "tıkla, sesli konuşalım";

  if (!wide) {
    return (
      <div className="flex justify-center border-t border-gray-200 py-3 dark:border-gray-800">
        <button onClick={() => assistantStore.toggleVoice()} title={voice ? "Sesli sohbeti bitir" : "Geolop Asistanı — sesli sohbet"}>
          <AssistantOrb state={orb} size={46} />
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-800">
      <button
        onClick={() => assistantStore.toggleVoice()}
        title={voice ? "Sesli sohbeti bitir" : "Sesli sohbeti başlat"}
        className={`flex w-full flex-col items-center gap-1 rounded-2xl border px-3 py-3 transition ${
          voice ? "border-brand-300 bg-brand-25 shadow-theme-md dark:border-brand-500/40 dark:bg-brand-500/10" : "border-gray-200 bg-gray-50 hover:border-brand-200 hover:bg-brand-25 dark:border-gray-800 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]"
        }`}
      >
        <AssistantOrb state={orb} size={104} />
        <span className="text-theme-sm font-semibold text-gray-800 dark:text-white/90">Geolop Asistanı</span>
        <span className={`text-theme-xs ${voice ? "text-brand-500" : "text-gray-400"}`}>{status}</span>
      </button>
      <button
        onClick={() => assistantStore.toggle()}
        className="mt-2 w-full rounded-lg border border-gray-200 py-1.5 text-theme-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-white/[0.03]"
      >
        {open ? "Sohbeti Kapat" : "Yazılı Sohbet"}
      </button>
    </div>
  );
}

function UserProfile({ wide }: { wide: boolean }) {
  const { data: user } = useMe();
  const displayName = user?.name?.trim() || user?.email || "Kullanıcı";
  const initials = displayName.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  // Google fotoğrafı yüklenemezse (URL süresi dolmuş / ağ engeli) baş harflere düş.
  const [avatarHata, setAvatarHata] = useState(false);
  const avatar = !avatarHata ? user?.avatarUrl : null;

  return (
    <div className={`flex items-center gap-3 border-t border-gray-200 py-3 dark:border-gray-800 ${wide ? "px-4" : "justify-center px-2"}`}>
      <Link href="/account">
        <div className="flex min-w-0 flex-1 cursor-pointer items-center gap-3" title="Hesabım — paket, token hakkı ve ayarlar">
          {avatar ? (
            <img
              src={avatar} alt="" referrerPolicy="no-referrer" onError={() => setAvatarHata(true)}
              className="h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-gray-200 dark:ring-gray-700"
            />
          ) : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-500 text-theme-xs font-bold text-white">
              {initials || "?"}
            </span>
          )}
          {wide && (
            <div className="min-w-0">
              <p className="truncate text-theme-sm font-medium text-gray-800 dark:text-white/90">{displayName}</p>
              <p className="truncate text-theme-xs text-gray-400">{user?.email ?? ""}</p>
              {user?.role && (
                <p className="truncate text-[10px] font-semibold tracking-wide text-gray-400 dark:text-gray-500">{user.role}</p>
              )}
            </div>
          )}
        </div>
      </Link>
      {wide && (
        <button onClick={() => logout()} title="Çıkış Yap" className="rounded-lg p-2 text-gray-400 hover:bg-error-50 hover:text-error-500 dark:hover:bg-error-500/10">
          <LogOut className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
